import type { ReactElement } from 'react';
import type { PostFormatDto } from '@gr/core/content/posts/dto';
import { useT } from '../../../../app/di';
import { formatDate } from '../../../../shared/format';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Button } from '../../../../ui-kit/Button/Button';
import { EmptyState } from '../../../../ui-kit/EmptyState/EmptyState';
import { Markdown } from '../../../../ui-kit/Markdown/Markdown';
import styles from './PostFormatList.module.scss';

export interface PostFormatListProps {
  formats: readonly PostFormatDto[];
  disabled: boolean;
  onEdit(format: PostFormatDto): void;
  onDelete(format: PostFormatDto): void;
  onMakeDefault(format: PostFormatDto): void;
  /** Действие пустого состояния — «добавить первое правило». */
  emptyAction?: ReactElement;
}

/** POST-UC-10: правила оформления блога — тип, отметка по умолчанию, дата изменения, текст. */
export function PostFormatList({ formats, disabled, onEdit, onDelete, onMakeDefault, emptyAction }: PostFormatListProps): ReactElement {
  const { t, locale } = useT();
  if (formats.length === 0) return <EmptyState title={t('posts.formats.empty')} text={t('posts.formats.emptyText')} action={emptyAction} />;
  return (
    <ul className={styles.list} aria-label={t('posts.formats.title')}>
      {formats.map((format) => (
        <li key={format.id} className={styles.item}>
          <div className={styles.header}>
            <div className={styles.titleLine}>
              <h3 className={styles.name}>{format.name}</h3>
              {format.isDefault ? <Badge tone="accent">{t('posts.formats.defaultBadge')}</Badge> : null}
            </div>
            <div className={styles.actions}>
              {format.isDefault ? null : (
                <Button size="sm" variant="ghost" disabled={disabled} onClick={() => onMakeDefault(format)}>
                  {t('posts.formats.makeDefault')}
                </Button>
              )}
              <Button size="sm" variant="ghost" icon="edit" disabled={disabled} onClick={() => onEdit(format)}>
                {t('posts.formats.edit')}
              </Button>
              <Button size="sm" variant="ghost" icon="trash" disabled={disabled} onClick={() => onDelete(format)}>
                {t('common.delete')}
              </Button>
            </div>
          </div>
          <p className="u-muted u-small">
            {t('posts.formats.updated', { date: formatDate(format.updatedAt, locale) })} · {t('posts.formats.count', { n: format.postCount })}
          </p>
          <Markdown text={format.rules} empty={t('posts.formats.emptyRules')} />
        </li>
      ))}
    </ul>
  );
}
